/**
 * Google Drive Service - Handles OAuth and file operations via Edge Functions
 *
 * Enables automatic backup uploads to Google Drive for F068 Enhanced Export.
 * Token exchange happens securely on the server via Supabase Edge Functions.
 */

import { getAccessToken } from '../lib/supabaseRest';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

const GOOGLE_AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';
const SCOPES = [
  'https://www.googleapis.com/auth/drive.file', // Access only app-created files
];

export interface DriveConnectionStatus {
  connected: boolean;
  isExpired: boolean;
  backupFolderId?: string;
  refreshTokenDaysRemaining?: number | null;
  oauthAppStatus?: 'testing' | 'production';
  needsReconnect?: boolean;
}

export interface TokenHealthInfo {
  connected: boolean;
  isExpired: boolean;
  accessTokenExpiresIn: number;
  refreshTokenDaysRemaining: number | null;
  needsReconnect: boolean;
  lastRefreshed: string | null;
  refreshCount: number;
  oauthAppStatus: 'testing' | 'production';
  error?: string;
}

export interface DriveFileInfo {
  id: string;
  name: string;
  size: string;
  createdTime: string;
  webContentLink?: string;
  folderId?: string;
}

export interface UploadResult {
  success: boolean;
  file?: DriveFileInfo;
  error?: string;
}

class GoogleDriveService {
  private connectionStatus: DriveConnectionStatus | null = null;

  /**
   * Get auth header for Edge Function calls
   */
  private async getAuthHeader(): Promise<string> {
    const accessToken = await getAccessToken();
    if (!accessToken) {
      throw new Error('Not logged in');
    }
    return `Bearer ${accessToken}`;
  }

  /**
   * Make authenticated request to Edge Function
   */
  private async edgeFetch(
    functionName: string,
    endpoint: string,
    options: RequestInit = {}
  ): Promise<Response> {
    const url = `${SUPABASE_URL}/functions/v1/${functionName}${endpoint}`;
    const authHeader = await this.getAuthHeader();
    return fetch(url, {
      ...options,
      headers: {
        'Authorization': authHeader,
        ...options.headers,
      },
    });
  }

  /**
   * Generate random state for OAuth CSRF protection
   */
  private generateState(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Initiate OAuth flow - redirects to Google
   */
  initiateOAuth(): void {
    if (!GOOGLE_CLIENT_ID) {
      throw new Error('Google Client ID not configured');
    }

    const redirectUri = `${window.location.origin}/oauth/google-callback`;
    const state = this.generateState();

    // Store state for verification
    sessionStorage.setItem('google_drive_oauth_state', state);

    const params = new URLSearchParams({
      client_id: GOOGLE_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: SCOPES.join(' '),
      access_type: 'offline',
      prompt: 'consent',
      state: state,
    });

    window.location.href = `${GOOGLE_AUTH_URL}?${params.toString()}`;
  }

  /**
   * Handle OAuth callback - exchange code for tokens via Edge Function
   */
  async handleOAuthCallback(code: string, state: string): Promise<boolean> {
    // Verify state
    const storedState = sessionStorage.getItem('google_drive_oauth_state');
    if (state !== storedState) {
      throw new Error('OAuth state mismatch - possible CSRF attack');
    }
    sessionStorage.removeItem('google_drive_oauth_state');

    const redirectUri = `${window.location.origin}/oauth/google-callback`;

    // Exchange code via Edge Function (keeps client_secret secure)
    const response = await this.edgeFetch('drive-oauth', '/exchange', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, redirectUri }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to connect Google Drive');
    }

    // Clear cached status to force refresh
    this.connectionStatus = null;
    return true;
  }

  /**
   * Check connection status from Edge Function
   */
  async getConnectionStatus(): Promise<DriveConnectionStatus> {
    try {
      const response = await this.edgeFetch('drive-oauth', '/status');

      if (!response.ok) {
        return { connected: false, isExpired: false };
      }

      const data = await response.json();
      this.connectionStatus = data;
      return data;
    } catch (error) {
      console.error('Error checking Drive connection:', error);
      return { connected: false, isExpired: false };
    }
  }

  /**
   * Get detailed token health information
   */
  async checkTokenHealth(): Promise<TokenHealthInfo> {
    try {
      const response = await this.edgeFetch('drive-oauth', '/health');

      if (!response.ok) {
        return {
          connected: false,
          isExpired: true,
          accessTokenExpiresIn: 0,
          refreshTokenDaysRemaining: null,
          needsReconnect: true,
          lastRefreshed: null,
          refreshCount: 0,
          oauthAppStatus: 'testing',
        };
      }

      return await response.json();
    } catch (error) {
      console.error('Error checking token health:', error);
      return {
        connected: false,
        isExpired: true,
        accessTokenExpiresIn: 0,
        refreshTokenDaysRemaining: null,
        needsReconnect: true,
        lastRefreshed: null,
        refreshCount: 0,
        oauthAppStatus: 'testing',
      };
    }
  }

  /**
   * Manually refresh access token
   */
  async refreshToken(): Promise<boolean> {
    try {
      const response = await this.edgeFetch('drive-oauth', '/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      return data.success && data.refreshed;
    } catch (error) {
      console.error('Error refreshing token:', error);
      return false;
    }
  }

  /**
   * Disconnect Google Drive
   */
  async disconnect(): Promise<boolean> {
    try {
      const response = await this.edgeFetch('drive-oauth', '/disconnect', {
        method: 'DELETE',
      });

      if (!response.ok) {
        return false;
      }

      // Clear cached status
      this.connectionStatus = null;
      return true;
    } catch (error) {
      console.error('Error disconnecting Drive:', error);
      return false;
    }
  }

  /**
   * Upload backup file to Google Drive
   */
  async uploadBackup(blob: Blob, backupName: string): Promise<UploadResult> {
    try {
      // Create form data
      const formData = new FormData();
      formData.append('file', blob, backupName);
      formData.append('fileName', backupName);

      // Upload via Edge Function
      const response = await this.edgeFetch('drive-api', '/upload', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        return {
          success: false,
          error: error.error || 'Failed to upload to Drive',
        };
      }

      const data = await response.json();
      return {
        success: true,
        file: data.file,
      };
    } catch (error) {
      console.error('Error uploading to Drive:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * List backups in Drive folder
   */
  async listBackups(): Promise<DriveFileInfo[]> {
    try {
      const response = await this.edgeFetch('drive-api', '/list');

      if (!response.ok) {
        console.error('Failed to list backups');
        return [];
      }

      const data = await response.json();
      return data.files || [];
    } catch (error) {
      console.error('Error listing backups:', error);
      return [];
    }
  }

  /**
   * Download backup from Drive
   */
  async downloadBackup(fileId: string): Promise<Blob | null> {
    try {
      const response = await this.edgeFetch('drive-api', `/download/${fileId}`);

      if (!response.ok) {
        console.error('Failed to download backup');
        return null;
      }

      return await response.blob();
    } catch (error) {
      console.error('Error downloading backup:', error);
      return null;
    }
  }

  /**
   * Delete backup from Drive
   */
  async deleteBackup(fileId: string): Promise<boolean> {
    try {
      const response = await this.edgeFetch('drive-api', `/delete/${fileId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error deleting backup:', error);
      return false;
    }
  }

  /**
   * Get or create "PKM Backups" folder (handled by Edge Function)
   * This is called automatically during upload
   */
  async getOrCreateBackupFolder(): Promise<string | null> {
    try {
      const status = await this.getConnectionStatus();
      return status.backupFolderId || null;
    } catch (error) {
      console.error('Error getting backup folder:', error);
      return null;
    }
  }

  // ============================================
  // Individual File Export Methods (F068)
  // ============================================

  /**
   * Upload markdown file to Drive
   * Creates new file or updates existing one
   */
  async uploadMarkdownFile(options: {
    name: string;
    content: string;
    mimeType: string;
    folderId: string;
    objectId?: string;
  }): Promise<{ success: boolean; fileId?: string; error?: string }> {
    try {
      const blob = new Blob([options.content], { type: options.mimeType });
      const formData = new FormData();
      formData.append('file', blob, options.name);
      formData.append('fileName', options.name);
      formData.append('folderId', options.folderId);
      formData.append('mimeType', options.mimeType);

      if (options.objectId) {
        formData.append('objectId', options.objectId);
      }

      const response = await this.edgeFetch('drive-api', '/file/upload-text', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        return {
          success: false,
          error: error.error || 'Failed to upload markdown file',
        };
      }

      const data = await response.json();
      return {
        success: true,
        fileId: data.fileId || data.file?.id,
      };
    } catch (error) {
      console.error('Error uploading markdown file:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Get or create folder by path
   * Supports nested paths like "notes/work"
   */
  async getOrCreateFolder(options: {
    path: string;
    parentFolderId: string;
  }): Promise<{ success: boolean; folderId?: string; error?: string }> {
    try {
      const response = await this.edgeFetch('drive-api', '/folder/get-or-create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          path: options.path,
          parentFolderId: options.parentFolderId,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        return {
          success: false,
          error: error.error || 'Failed to get or create folder',
        };
      }

      const data = await response.json();
      return {
        success: true,
        folderId: data.folderId,
      };
    } catch (error) {
      console.error('Error getting or creating folder:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Create folder in Drive
   */
  async createFolder(
    name: string,
    parentFolderId: string
  ): Promise<{ success: boolean; folderId?: string; error?: string }> {
    try {
      const response = await this.edgeFetch('drive-api', '/folder/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, parentFolderId }),
      });

      if (!response.ok) {
        const error = await response.json();
        return {
          success: false,
          error: error.error || 'Failed to create folder',
        };
      }

      const data = await response.json();
      return {
        success: true,
        folderId: data.folderId,
      };
    } catch (error) {
      console.error('Error creating folder:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Find file by name in folder
   */
  async findFileByName(
    name: string,
    folderId: string
  ): Promise<string | null> {
    try {
      const response = await this.edgeFetch('drive-api', '/file/find', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, folderId }),
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      return data.fileId || null;
    } catch (error) {
      console.error('Error finding file:', error);
      return null;
    }
  }

  /**
   * Update existing file content
   */
  async updateFileContent(
    fileId: string,
    content: string,
    mimeType: string
  ): Promise<boolean> {
    try {
      const blob = new Blob([content], { type: mimeType });
      const formData = new FormData();
      formData.append('file', blob);
      formData.append('fileId', fileId);
      formData.append('mimeType', mimeType);

      const response = await this.edgeFetch('drive-api', '/file/update', {
        method: 'PATCH',
        body: formData,
      });

      return response.ok;
    } catch (error) {
      console.error('Error updating file content:', error);
      return false;
    }
  }

  /**
   * Delete file from Drive
   */
  async deleteFile(fileId: string): Promise<boolean> {
    try {
      const response = await this.edgeFetch('drive-api', `/file/delete/${fileId}`, {
        method: 'DELETE',
      });

      return response.ok;
    } catch (error) {
      console.error('Error deleting file:', error);
      return false;
    }
  }

  /**
   * Execute batch operations
   * Allows multiple operations in a single request
   */
  async executeBatch(operations: Array<{
    type: 'upload' | 'update' | 'delete';
    fileId?: string;
    fileName?: string;
    content?: string;
    folderId?: string;
  }>): Promise<{
    success: boolean;
    results?: Array<{ success: boolean; fileId?: string; error?: string }>;
    error?: string;
  }> {
    try {
      const response = await this.edgeFetch('drive-api', '/batch/execute', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ operations }),
      });

      if (!response.ok) {
        const error = await response.json();
        return {
          success: false,
          error: error.error || 'Batch operation failed',
        };
      }

      const data = await response.json();
      return {
        success: true,
        results: data.results,
      };
    } catch (error) {
      console.error('Error executing batch:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }
}

// Export singleton instance
export const googleDriveService = new GoogleDriveService();
